import React from 'react';
import { ActivityItem, Icon, IconButton, Spinner, SpinnerSize, Stack } from '@fluentui/react';
import { useGetStorefrontOrderActivitiesQuery } from '../../../store/endpoints/StorefrontSettingsEndpoints';
import { IStorefrontOrderActivity } from '../../../models/Storefront';
import { STOREFRONT_ORDER_ACTIVITY_TYPE, StorefrontOrderActivityDisplay } from '../../../Constants';

interface IStorefrontOrderActivitiesProps {
    orderId: string;
}

const StorefrontOrderActivities: React.FC<IStorefrontOrderActivitiesProps> = ({ orderId }) => {
    const { data, isFetching, refetch } = useGetStorefrontOrderActivitiesQuery({ id: orderId }, { skip: !orderId });

    return (
        <Stack>
            <Stack horizontal verticalAlign='center'>
                <span style={{ fontWeight: 600, flexGrow: 1 }}>Activity</span>
                <IconButton
                    iconProps={{ iconName: 'Refresh' }}
                    title='Refresh'
                    disabled={isFetching}
                    onClick={refetch}
                />
            </Stack>
            {isFetching ? (
                <Spinner
                    label='Loading activity...'
                    size={SpinnerSize.large}
                    style={{ marginTop: '3em' }}
                    styles={{
                        circle: { width: '60px', height: '60px', borderWidth: '4px' }
                    }}
                />
            ) : !data?.items?.length ? (
                <p style={{ color: '#605e5c', marginTop: '1em' }}>No activity recorded for this order.</p>
            ) : (
                <Stack tokens={{ childrenGap: '1em' }} style={{ marginTop: '0.5em' }}>
                    {data.items.map(activity => (
                        <StorefrontOrderActivityRow key={activity.id} activity={activity} />
                    ))}
                </Stack>
            )}
        </Stack>
    );
};

interface IStorefrontOrderActivityRowProps {
    activity: IStorefrontOrderActivity;
}

const activityIcon = (activity: IStorefrontOrderActivity): string => {
    if (activity.success === false) return 'ErrorBadge';
    switch (activity.activityType) {
        case STOREFRONT_ORDER_ACTIVITY_TYPE.ORDER_PLACED:
            return 'ShoppingCart';
        case STOREFRONT_ORDER_ACTIVITY_TYPE.EMAIL_SENT:
            return 'Mail';
        case STOREFRONT_ORDER_ACTIVITY_TYPE.OPPORTUNITY_CREATED:
            return 'Lightbulb';
        case STOREFRONT_ORDER_ACTIVITY_TYPE.DEMAND_ITEMS_CREATED:
            return 'ProductList';
        case STOREFRONT_ORDER_ACTIVITY_TYPE.PROCUREMENT_ORDER_CREATED:
            return 'AddToShoppingList';
        case STOREFRONT_ORDER_ACTIVITY_TYPE.SALES_ORDER_CREATED:
            return 'Receipt';
        case STOREFRONT_ORDER_ACTIVITY_TYPE.WEBHOOK_SENT:
            return 'PlugConnected';
        case STOREFRONT_ORDER_ACTIVITY_TYPE.STATUS_UPDATED:
            return 'Edit';
        case STOREFRONT_ORDER_ACTIVITY_TYPE.SHIPMENT_ADDED:
        case STOREFRONT_ORDER_ACTIVITY_TYPE.SHIPMENT_UPDATED:
            return 'DeliveryTruck';
        case STOREFRONT_ORDER_ACTIVITY_TYPE.SHIPMENT_REMOVED:
            return 'Cancel';
        default:
            return 'Info';
    }
};

const StorefrontOrderActivityRow: React.FC<IStorefrontOrderActivityRowProps> = ({ activity }) => {
    const commentStyle: React.CSSProperties = { marginTop: '0.25em', marginBottom: '0.25em' };
    const failed = activity.success === false;

    const description = (
        <>
            {activity.user && <><strong>{activity.user}</strong>&nbsp;</>}
            {StorefrontOrderActivityDisplay(activity.activityType)}
            {failed ? ' failed' : ''}.
        </>
    );

    const comments: React.ReactNode[] = [];
    if (activity.message) {
        comments.push(<p key='msg' style={commentStyle}>{activity.message}</p>);
    }
    if (failed && activity.error) {
        comments.push(<p key='err' style={{ ...commentStyle, color: '#a4262c' }}>{activity.error}</p>);
    }
    if (activity.changedFields?.length) {
        comments.push(
            <p key='fields' style={{ ...commentStyle, display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {activity.changedFields.map(f => (
                    <span key={f} style={{
                        background: '#f3f2f1',
                        border: '1px solid #d2d0ce',
                        borderRadius: '2px',
                        padding: '1px 6px',
                        fontSize: '12px',
                        fontFamily: 'inherit'
                    }}>{f}</span>
                ))}
            </p>
        );
    }

    return (
        <ActivityItem
            activityDescription={description}
            comments={comments.length ? comments : undefined}
            timeStamp={new Date(activity.timestamp).toLocaleString()}
            activityIcon={<Icon iconName={activityIcon(activity)} style={failed ? { color: '#a4262c' } : undefined} />}
        />
    );
};

export default StorefrontOrderActivities;
